/* eslint-disable react-refresh/only-export-components */
import { createContext, useState, useEffect, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { User } from '@/types';
import { authService, LoginCredentials, LoginResponse } from '@/services/authService';
import { authHandler } from '@/utils/authHandler';

// Define the context type
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<LoginResponse>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<boolean>;
}

// Create the context with a default value
export const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  login: async () => ({ success: false, message: 'Not implemented' }),
  logout: async () => {},
  refreshAuth: async () => false,
});

// Create a provider component
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const login = async (credentials: LoginCredentials): Promise<LoginResponse> => {
    try {
      setIsLoading(true);
      const response = await authService.login(credentials);

      if (response.success && response.user) {
        setUser(response.user);
        // Store user data in localStorage for persistence across tabs
        localStorage.setItem('user', JSON.stringify(response.user));
      }

      return response;
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        message: 'Login failed due to network error',
      };
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async (): Promise<void> => {
    try {
      // Call API logout to clear HTTP-only cookie
      await authService.logout();
    } catch (error) {
      console.error('Logout API call failed:', error);
    } finally {
      // Always clear local state
      setUser(null);
      localStorage.removeItem('user');
    }
  };

  const refreshAuth = async (): Promise<boolean> => {
    try {
      setIsLoading(true);
      const response = await authService.refreshToken();

      if (response.success && response.user) {
        setUser(response.user);
        localStorage.setItem('user', JSON.stringify(response.user));
        return true;
      } else {
        // Clear invalid session
        setUser(null);
        localStorage.removeItem('user');
        return false;
      }
    } catch (error) {
      console.error('Auth refresh failed:', error);
      setUser(null);
      localStorage.removeItem('user');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize authentication state on app load
  useEffect(() => {
    const initializeAuth = async () => {
      // First, check if we have user data in localStorage
      const storedUser = localStorage.getItem('user');

      if (storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          const isValid = await refreshAuth();
          if (!isValid) {
            console.log('Session expired, user needs to log in again');
          }
          setUser(userData);
        } catch (error) {
          console.error('Failed to parse stored user data:', error);
          localStorage.removeItem('user');
          setIsLoading(false);
        }
      } else {
        // No stored user, check if there's a valid session cookie
        const isValid = await refreshAuth();

        if (!isValid) {
          setIsLoading(false);
        }
      }
    };

    initializeAuth();
  }, []);

  // Set up periodic token refresh (every 23 hours to stay ahead of 24-hour expiration)
  useEffect(() => {
    if (user && !isLoading) {
      const refreshInterval = setInterval(() => {
        refreshAuth().catch(error => {
          console.error('Periodic auth refresh failed:', error);
        });
      }, 23 * 60 * 60 * 1000); // 23 hours

      return () => clearInterval(refreshInterval);
    }
  }, [user, isLoading]);

  // Register logout handler with auth handler for API interceptor
  useEffect(() => {
    authHandler.setLogoutHandler(logout);

    return () => {
      authHandler.clearLogoutHandler();
    };
  }, []);  // Empty dependency array since logout function is stable

  // Keep Sentry user context in sync with the authenticated user
  useEffect(() => {
    if (!user) {
      Sentry.setUser(null);
      return;
    }

    Sentry.setUser({
      id: String(user.id),
      username: user.username,
      name: user.fullName,
      email: user.email,
    });
  }, [user]);

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    refreshAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
